import { ChannelIcon } from '@/components/ui/channel-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

import { CardButton } from './card-button';
import { useCardContent } from './use-card-content';

interface InactiveChannelCardProps {
  channel: string;
  openingId?: string;
}

// Pick the narrowest width that fits the description in the fixed 220px height
// without overflowing. Buckets are calibrated against current English/Spanish
// copy in useCardContent. If a future translation overflows, bump the relevant
// threshold rather than introducing per-channel overrides.
function getCardWidth(descriptionLength: number): number {
  if (descriptionLength > 140) return 300;
  if (descriptionLength > 110) return 280;
  if (descriptionLength > 80) return 250;
  return 220;
}

export function InactiveChannelCard({
  channel,
  openingId,
}: InactiveChannelCardProps) {
  const content = useCardContent(channel);

  if (!content) return null;

  const { title, description } = content;

  return (
    <div
      style={{ width: getCardWidth(description.length) }}
      className="shadow-base flex h-[220px] shrink-0 flex-col items-center justify-between gap-2 rounded-lg bg-white p-4 pt-6"
    >
      <ChannelIcon channelSlug={channel} variant="large" />
      <div className="flex flex-col items-center py-2">
        <Typography variant="small2" className="!text-fx-neutral-600">
          {title}
        </Typography>
        <Typography
          variant="small1"
          className="!text-fx-neutral-600 text-center"
        >
          {description}
        </Typography>
      </div>
      <CardButton channel={channel} openingId={openingId} />
    </div>
  );
}

InactiveChannelCard.Skeleton = function InactiveChannelCardSkeleton() {
  return (
    <div className="shadow-base flex h-[220px] w-[250px] shrink-0 flex-col items-center justify-between gap-2 rounded-lg bg-white p-4 pt-6">
      <Skeleton className="h-6 w-25" />
      <div className="flex flex-col items-center gap-1 py-2">
        <Skeleton className="h-4 w-25" />
        <Skeleton className="h-3 w-30" />
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-15" />
      </div>
      <Skeleton className="h-9 w-28" />
    </div>
  );
};
